import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  InputBase,
  Drawer,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stepper,
  Step,
  StepLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Switch,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
} from '@mui/x-data-grid';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import FolderIcon from '@mui/icons-material/Folder';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import WarningIcon from '@mui/icons-material/Warning';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import SaveIcon from '@mui/icons-material/Save';
import { useNavigate } from 'react-router-dom';

const PDFViewer = ({ fileUrl, fileName, onBack, alerts }) => {
  const comparisonData = [
    { fileName: 'Opus Elevated, LP', date: '7/21/25', value: '10,000', compareValue: '10,000', delta: '0' },
    { fileName: 'Beginning Balance', date: '7/21/25', value: '1,000,000', compareValue: '1,000,000', delta: '0' },
    { fileName: 'Commitment', date: '7/21/25', value: '1,233,221', compareValue: '1,233,221', delta: '0' },
    { fileName: 'Contributions', date: '7/21/25', value: '45,354', compareValue: '45,354', delta: '0' },
    { fileName: 'Distributions', date: '7/21/25', value: '54,632', compareValue: '54,632', delta: '0' },
    { fileName: 'Total Expense', date: '7/21/25', value: '43,106', compareValue: '43,106', delta: '0' },
    { fileName: 'Ending NAV', date: '7/21/25', value: '1,926,326,422', compareValue: '1,926,326,422', delta: '0' },
    { fileName: 'Opus Elevated, LP', date: '7/21/25', value: '10,000', compareValue: '10,000', delta: '0' },
    { fileName: 'Beginning Balance', date: '7/21/25', value: '1,000,000', compareValue: '1,000,000', delta: '0' },
    { fileName: 'Commitment', date: '7/21/25', value: '1,233,221', compareValue: '1,233,221', delta: '0' },
    { fileName: 'Contributions', date: '7/21/25', value: '45,354', compareValue: '45,354', delta: '0' },
    { fileName: 'Distributions', date: '7/21/25', value: '54,632', compareValue: '54,632', delta: '0' },
    { fileName: 'Total Expense', date: '7/21/25', value: '43,106', compareValue: '43,106', delta: '0' },
    { fileName: 'Ending NAV', date: '7/21/25', value: '1,926,326,422', compareValue: '1,926,326,422', delta: '0' },
  ];

  // State for stepper data
  const [steps, setSteps] = useState([]);
  const [stepperLoading, setStepperLoading] = useState(true);
  const [stepperError, setStepperError] = useState(null);
  const [activeStep, setActiveStep] = useState(-1);

  const [investorNumber, setInvestorNumber] = useState(1);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [savedViews, setSavedViews] = useState([]);
  const [viewName, setViewName] = useState('');

  // Fetch workflow steps from API
  useEffect(() => {
    const fetchWorkflowSteps = async () => {
      try {
        setStepperLoading(true);
        const token = localStorage.getItem('token');
        
        if (!token) {
          throw new Error('No authentication token found');
        }

        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/workflow-steps`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        
        // Sort steps by stepOrder and extract stepName
        const sortedSteps = data
          .sort((a, b) => a.stepOrder - b.stepOrder)
          .map(step => step.stepName);

        setSteps(sortedSteps);
        setStepperError(null);
      } catch (err) {
        console.error('Error fetching workflow steps:', err);
        setStepperError(err.message);
        // Fallback to hardcoded steps if API fails
        setSteps([
          'Data Integrity Checks Quarters',
          'Data Integrity Checks Year',
          'Data Integrity Checks Inception',
          'Write Reference Data',
          'Roll YTD/ITD with QTD Changes',
          'Summary',
        ]);
      } finally {
        setStepperLoading(false);
      }
    };

    fetchWorkflowSteps();
  }, []);

  const handleIncrement = () => setInvestorNumber((prev) => prev + 1);
  const handleDecrement = () => setInvestorNumber((prev) => (prev > 1 ? prev - 1 : 1));

  const columns = [
    { field: 'fileName', headerName: 'File Name', width: 200, filterable: true },
    { field: 'date', headerName: 'Date', width: 120, type: 'date', valueGetter: ({ value }) => new Date(value) },
    { field: 'value', headerName: 'Value', width: 150 },
    { field: 'compareValue', headerName: 'Compare Value', width: 150 },
    { field: 'delta', headerName: 'Delta', width: 120, renderCell: (params) => (
      <Typography sx={{ fontSize: '0.7rem', color: params.value === '0' ? 'text.primary' : 'red' }}>
        {params.value}
      </Typography>
    ) },
  ];

  const handleSaveView = () => {
    if (viewName) {
      setSavedViews([...savedViews, { name: viewName, columns: columnVisibilityModel }]);
      setViewName('');
    }
  };

  const handleViewSelect = (view) => {
    setColumnVisibilityModel(view.columns);
  };

  const CustomToolbar = () => (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
        <InputBase
          placeholder="View Name"
          value={viewName}
          onChange={(e) => setViewName(e.target.value)}
          sx={{ fontSize: '0.75rem', border: '1px solid #e0e0e0', borderRadius: 1, px: 1 }}
        />
        <IconButton onClick={handleSaveView} disabled={!viewName} sx={{ color: '#66BB6A' }}>
          <SaveIcon />
        </IconButton>
      </Box>
      <Box sx={{ ml: 2 }}>
        {savedViews.map((view, index) => (
          <Button key={index} onClick={() => handleViewSelect(view)} sx={{ fontSize: '0.75rem', textTransform: 'none', mr: 1 }}>
            {view.name}
          </Button>
        ))}
      </Box>
    </GridToolbarContainer>
  );

  return (
    <Box sx={{ bgcolor: '#f8f9fa', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: 'white', borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ color: '#66BB6A', textTransform: 'none', fontWeight: 400, fontSize: '0.65rem', p: 0.5 }}>
            Back to Workflows
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
            {fileName}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, maxWidth: '70%', mx: 2 }}>
          {stepperLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
              <CircularProgress size={20} sx={{ color: '#66BB6A' }} />
              <Typography variant="caption" sx={{ ml: 1, fontSize: '0.55rem' }}>
                Loading workflow steps...
              </Typography>
            </Box>
          ) : stepperError ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
              <Typography variant="caption" color="error" sx={{ fontSize: '0.55rem' }}>
                Error loading steps: {stepperError}
              </Typography>
            </Box>
          ) : (
            <Stepper activeStep={activeStep} alternativeLabel sx={{ '& .MuiStepLabel-label': { fontSize: '0.55rem' }, '& .MuiStepIcon-root': { width: '1rem', height: '1rem' } }}>
              {steps.map((label, index) => (
                <Step key={index}>
                  <StepLabel sx={{ '& .MuiStepLabel-label': { fontSize: '0.55rem' } }}>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          )}
        </Box>
        <Button onClick={() => setAlertsOpen(true)} sx={{ color: '#66BB6A', textTransform: 'none', fontSize: '0.65rem' }}>
          View Alerts
        </Button>
      </Box>
      <Box sx={{ display: 'flex', flex: 1, p: 2, gap: 2 }}>
        <Box sx={{ flex: 2, maxWidth: '50%' }}>
          <Box sx={{ width: '100%', height: '800px', border: '1px solid #ccc' }}>
            <iframe src={`${fileUrl}#toolbar=1&navpanes=1&scrollbar=1`} title={`PDF Viewer - ${fileName}`} style={{ width: '100%', height: '100%', border: 'none' }} />
          </Box>
        </Box>
        <Box sx={{ flex: 1, maxWidth: '50%', height: '800px', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ fontSize: '0.75rem', mr: 1 }}>
                Investor:
              </Typography>
              <IconButton onClick={handleDecrement} sx={{ bgcolor: '#f0f0f0', width: '24px', height: '24px', '&:hover': { bgcolor: '#e0e0e0' } }}>
                <RemoveIcon sx={{ fontSize: '16px' }} />
              </IconButton>
              <Typography sx={{ fontSize: '0.75rem', minWidth: '30px', textAlign: 'center' }}>
                {investorNumber}
              </Typography>
              <IconButton onClick={handleIncrement} sx={{ bgcolor: '#f0f0f0', width: '24px', height: '24px', '&:hover': { bgcolor: '#e0e0e0' } }}>
                <AddIcon sx={{ fontSize: '16px' }} />
              </IconButton>
            </Box>
          </Box>
          <Paper elevation={0} sx={{ flex: 1, borderRadius: 2, overflow: 'hidden', boxShadow: '0px 2px 8px rgba(0,0,0,0.05)' }}>
            <DataGrid
              rows={comparisonData}
              columns={columns}
              components={{ Toolbar: CustomToolbar }}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
              sx={{ border: '1px solid #e0e0e0', backgroundColor: '#f8f9fa' }}
              getRowId={(row) => `${row.fileName}-${row.date}-${row.value}`}
              initialState={{
                filter: {
                  filterModel: {
                    items: [
                      { columnField: 'fileName', operatorValue: 'contains', value: '' },
                      { columnField: 'date', operatorValue: 'equals', value: null },
                    ],
                  },
                },
              }}
            />
          </Paper>
        </Box>
      </Box>
      <Drawer
        anchor="right"
        open={alertsOpen}
        onClose={() => setAlertsOpen(false)}
        sx={{ '& .MuiDrawer-paper': { width: 500, boxSizing: 'border-box', p: 2 } }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Alerts</Typography>
          <IconButton onClick={() => setAlertsOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 2 }} />
        {alerts.map((alert, index) => (
          <Accordion key={index} sx={{ mb: 1, border: '1px solid #e0e0e0', borderRadius: '2px' }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ fontSize: '24px', color: '#757575' }} />}
              sx={{
                bgcolor: '#f5f5f5',
                color: '#424242',
                minHeight: '28px',
                '& .MuiAccordionSummary-content': { margin: '2px 0' },
                '& .MuiAccordionSummary-expandIconWrapper': { margin: '0 2px' }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <WarningIcon sx={{ fontSize: '14px', color: '#ff0000' }} />
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#424242' }}>
                  {alert.investorName}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ bgcolor: '#fff', p: 0.25 }}>
              <Typography sx={{ fontSize: '0.7rem', color: '#757575' }}>
                {alert.errorDetails}
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: '#757575', mt: 0.5 }}>
                Metadata: {alert.metadata}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Drawer>
    </Box>
  );
};

const Workflows = () => {
  const navigate = useNavigate();
  const [quarterAnchorEl, setQuarterAnchorEl] = useState(null);
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentView, setCurrentView] = useState('workflows');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [savedViews, setSavedViews] = useState([]);
  const [viewName, setViewName] = useState('');

  // API State
  const [workflowData, setWorkflowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Side drawer API state
  const [drawerFiles, setDrawerFiles] = useState([]);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerError, setDrawerError] = useState(null);

  // Fetch workflow data from API
  useEffect(() => {
    const fetchWorkflowData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        if (!token) {
          throw new Error('No authentication token found');
        }

        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/tenant-documents/summary-by-document-type`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        
        // Transform API data to match component structure
        const transformedData = data.map((item, index) => ({
          id: index + 1,
          documentType: item.documentTypeName,
          inProcess: item.inProcess,
          awaitingApproval: item.awaitingApproval,
          complete: item.complete,
        }));

        setWorkflowData(transformedData);
        setError(null);
      } catch (err) {
        console.error('Error fetching workflow data:', err);
        setError(err.message);
        // Fallback to hardcoded data if API fails
        setWorkflowData([
          { id: 1, documentType: 'Internal Capital Statements', inProcess: 2, awaitingApproval: 0, complete: 100 },
          { id: 2, documentType: 'Wire SVB', inProcess: 0, awaitingApproval: 0, complete: 48599 },
          { id: 3, documentType: 'Internal C/O', inProcess: 0, awaitingApproval: 0, complete: 5543 },
          { id: 4, documentType: 'Fund of Funds FCIPs', inProcess: 6, awaitingApproval: 0, complete: 41408 },
          { id: 5, documentType: 'Holdings', inProcess: 0, awaitingApproval: 1, complete: 465844 },
          { id: 6, documentType: 'Uncategorized', inProcess: 0, awaitingApproval: 0, complete: 10 },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchWorkflowData();
  }, []);

  // Fetch file instances for selected document type
  const fetchFileInstances = async () => {
    try {
      setDrawerLoading(true);
      setDrawerError(null);
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/file-instances`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('API Response:', data);
      
      // Transform API data - using real API data now
      const transformedFiles = data.map((file, index) => {
        // Generate some sample dates, process steps, and terms for demo purposes
        const sampleData = [
          { date: '2/23/25', idTerm: 'Cadence Line, LP', processStep: 'Error', hasError: true },
          { date: '7/21/25', idTerm: 'Opus Elevated, LP', processStep: 'In Progress', hasError: false },
          { date: '6/17/25', idTerm: 'Alto Heights, LP', processStep: 'Complete', hasError: false },
          { date: '9/30/25', idTerm: 'Summit Capital, LP', processStep: 'Complete', hasError: false },
          { date: '1/25/25', idTerm: 'Vertex Funds, LP', processStep: 'Awaiting Approval', hasError: false },
          { date: '3/15/25', idTerm: 'Phoenix Investment, LP', processStep: 'In Progress', hasError: false },
          { date: '5/10/25', idTerm: 'Sterling Holdings, LP', processStep: 'Complete', hasError: false },
          { date: '8/22/25', idTerm: 'Meridian Capital, LP', processStep: 'Error', hasError: true },
        ];
        
        const sampleItem = sampleData[index % sampleData.length];
        
        return {
          id: file.fileInstanceId || index + 1,
          name: file.fileName || `Unknown File ${index + 1}`,
          url: file.s3RawLocation || 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
          status: sampleItem.processStep,
          detectedType: file.fileType?.toUpperCase() || 'PDF',
          date: sampleItem.date,
          idTerm: sampleItem.idTerm,
          processStep: sampleItem.processStep,
          hasError: sampleItem.hasError,
          fileSize: file.fileSizeBytes,
          tenantId: file.tenantId,
          batchInstanceId: file.batchInstanceId,
        };
      });

      console.log('Transformed Files:', transformedFiles);
      setDrawerFiles(transformedFiles);
    } catch (err) {
      console.error('Error fetching file instances:', err);
      setDrawerError(err.message);
      
      // Fallback to a simple hardcoded list if API fails
      const fallbackFiles = [
        { 
          id: 1, 
          name: 'API_Unavailable_File_1.pdf', 
          date: '2/23/25', 
          idTerm: 'Cadence Line, LP', 
          processStep: 'Error', 
          hasError: true, 
          url: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf', 
          detectedType: 'PDF' 
        },
        { 
          id: 2, 
          name: 'API_Unavailable_File_2.pdf', 
          date: '7/21/25', 
          idTerm: 'Opus Elevated, LP', 
          processStep: 'In Progress', 
          hasError: false, 
          url: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf', 
          detectedType: 'PDF' 
        },
      ];
      setDrawerFiles(fallbackFiles);
    } finally {
      setDrawerLoading(false);
    }
  };

  const alerts = [
    { investorName: 'Opus Elevated, LP', errorDetails: 'Extracted Accounted Income Allocation Current Period Change does not match reference data', metadata: 'Distributions mismatch' },
    { investorName: 'Cadence Line, LP', errorDetails: 'Distributions do not match reference data', metadata: 'Value discrepancy' },
  ];

  const handleQuarterClick = (event) => setQuarterAnchorEl(event.currentTarget);
  const handleQuarterClose = () => setQuarterAnchorEl(null);

  const handleOpenUploadDialog = () => setOpenUploadDialog(true);
  const handleCloseUploadDialog = () => {
    setOpenUploadDialog(false);
    setUploadFiles([]);
    setUploading(false);
    setUploadProgress(0);
  };

  const handleFileChange = (event) => {
    if (event.target.files) {
      const newFiles = Array.from(event.target.files).map(file => ({
        name: file.name,
        size: file.size,
        type: file.type,
        detectedType: file.type === 'application/pdf' ? 'PDF' : 'Unknown',
        file: file,
      }));
      setUploadFiles(newFiles);
    }
  };

  const handleFileDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer.files) {
      const newFiles = Array.from(event.dataTransfer.files).map(file => ({
        name: file.name,
        size: file.size,
        type: file.type,
        detectedType: file.type === 'application/pdf' ? 'PDF' : 'Unknown',
        file: file,
      }));
      setUploadFiles(newFiles);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleUploadClick = () => {
    if (uploadFiles.length > 0) {
      setUploading(true);
      let progress = 0;
      const interval = setInterval(() => {
        progress += 10;
        setUploadProgress(progress);
        if (progress >= 100) {
          clearInterval(interval);
          setTimeout(() => handleCloseUploadDialog(), 500);
        }
      }, 300);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    else return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const handleRowDoubleClick = (documentType) => {
    setSelectedDocumentType(documentType);
    setDrawerOpen(true);
    // Fetch file instances when drawer opens
    fetchFileInstances();
  };

  const handleFileClick = (params) => {
    setSelectedFile(params.row);
    setCurrentView('pdfViewer');
    setDrawerOpen(false);
  };

  const handleBackToWorkflows = () => {
    setCurrentView('workflows');
    setSelectedFile(null);
  };

  const handleSaveView = () => {
    if (viewName) {
      setSavedViews([...savedViews, { name: viewName, columns: columnVisibilityModel }]);
      setViewName('');
    }
  };

  const handleViewSelect = (view) => {
    setColumnVisibilityModel(view.columns);
  };

  const columns = [
    { field: 'name', headerName: 'File Name', width: 200, filterable: true },
    { field: 'date', headerName: 'Date', width: 120, type: 'date', valueGetter: ({ value }) => new Date(value) },
    { field: 'idTerm', headerName: 'ID Term', width: 150 },
    {
      field: 'processStep',
      headerName: 'Process Step',
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {params.row.hasError && <WarningIcon sx={{ fontSize: '14px', color: '#ff0000' }} />}
          <Typography sx={{ fontSize: '0.7rem', color: params.row.hasError ? '#ff0000' : params.row.processStep === 'In Progress' ? '#000' : '#4caf50' }}>
            {params.row.processStep}
          </Typography>
        </Box>
      ),
    },
  ];

  const CustomToolbar = () => (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
        <InputBase
          placeholder="View Name"
          value={viewName}
          onChange={(e) => setViewName(e.target.value)}
          sx={{ fontSize: '0.75rem', border: '1px solid #e0e0e0', borderRadius: 1, px: 1 }}
        />
        <IconButton onClick={handleSaveView} disabled={!viewName} sx={{ color: '#66BB6A' }}>
          <SaveIcon />
        </IconButton>
      </Box>
      <Box sx={{ ml: 2 }}>
        {savedViews.map((view, index) => (
          <Button key={index} onClick={() => handleViewSelect(view)} sx={{ fontSize: '0.75rem', textTransform: 'none', mr: 1 }}>
            {view.name}
          </Button>
        ))}
      </Box>
    </GridToolbarContainer>
  );

  if (loading) {
    return (
      <Box sx={{ p: 3, bgcolor: '#f8f9fa', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress sx={{ color: '#66BB6A' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, bgcolor: '#f8f9fa', minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
        <Typography variant="h6" color="error" sx={{ mb: 2 }}>
          Error loading workflow data
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {error}
        </Typography>
        <Button 
          variant="contained" 
          onClick={() => window.location.reload()} 
          sx={{ bgcolor: '#66BB6A', '&:hover': { bgcolor: '#5aa55a' } }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: '#f8f9fa', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {currentView === 'workflows' ? (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
              <b>Workflows</b>
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleOpenUploadDialog}
                sx={{ bgcolor: '#8CD867', '&:hover': { bgcolor: '#7bc45a' }, textTransform: 'none', borderRadius: 28, px: 3, py: 1, fontWeight: 400 }}
              >
                File Upload
              </Button>
              <Button
                variant="contained"
                endIcon={<KeyboardArrowDownIcon />}
                onClick={handleQuarterClick}
                sx={{ bgcolor: '#f0f0f0', color: 'text.primary', '&:hover': { bgcolor: '#e0e0e0' }, textTransform: 'none', borderRadius: 28, px: 3, py: 1, boxShadow: 'none', fontWeight: 400 }}
              >
                Q2 2025
              </Button>
              <Menu anchorEl={quarterAnchorEl} open={Boolean(quarterAnchorEl)} onClose={handleQuarterClose}>
                <MenuItem onClick={handleQuarterClose}>Q1 2025</MenuItem>
                <MenuItem onClick={handleQuarterClose}>Q2 2025</MenuItem>
                <MenuItem onClick={handleQuarterClose}>Q3 2025</MenuItem>
                <MenuItem onClick={handleQuarterClose}>Q4 2025</MenuItem>
              </Menu>
            </Box>
          </Box>
          <Paper elevation={0} sx={{ flex: 1, borderRadius: 2, overflow: 'hidden', boxShadow: '0px 2px 8px rgba(0,0,0,0.05)', mb: 3 }}>
            <DataGrid
              rows={workflowData}
              columns={[
                { field: 'documentType', headerName: 'Document Type', flex: 1 },
                { field: 'inProcess', headerName: 'In Process', width: 120, align: 'center' },
                { field: 'awaitingApproval', headerName: 'Awaiting Approval', width: 120, align: 'center' },
                { field: 'complete', headerName: 'Complete', width: 120, align: 'right' },
              ]}
              onRowDoubleClick={(params) => handleRowDoubleClick(params.row.documentType)}
              sx={{ border: 'none', '& .MuiDataGrid-row:hover': { bgcolor: '#f5f5f5', cursor: 'pointer' } }}
              disableSelectionOnClick
            />
          </Paper>
          <Drawer
            anchor="right"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            sx={{ '& .MuiDrawer-paper': { width: isFullScreen ? '100%' : '60%', boxSizing: 'border-box', p: 3 } }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">{selectedDocumentType} Files</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <FormControlLabel
                  control={<Switch checked={isFullScreen} onChange={() => setIsFullScreen(!isFullScreen)} />}
                  label={isFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                  labelPlacement="start"
                  sx={{ m: 0 }}
                />
                <IconButton onClick={() => setDrawerOpen(false)}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>
            <Divider sx={{ mb: 2 }} />
            
            {/* Loading state for drawer */}
            {drawerLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                <CircularProgress sx={{ color: '#66BB6A' }} />
                <Typography variant="body2" sx={{ ml: 2 }}>Loading files...</Typography>
              </Box>
            ) : drawerError ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '200px', justifyContent: 'center' }}>
                <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                  Error loading files: {drawerError}
                </Typography>
                <Button 
                  variant="outlined" 
                  onClick={fetchFileInstances}
                  sx={{ color: '#66BB6A', borderColor: '#66BB6A' }}
                >
                  Retry
                </Button>
              </Box>
            ) : (
              <DataGrid
                rows={drawerFiles}
                columns={columns}
                components={{ Toolbar: CustomToolbar }}
                onRowClick={handleFileClick}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
                sx={{ border: '1px solid #e0e0e0', backgroundColor: '#f8f9fa' }}
                initialState={{
                  filter: {
                    filterModel: {
                      items: [
                        { columnField: 'name', operatorValue: 'contains', value: '' },
                        { columnField: 'date', operatorValue: 'equals', value: null },
                      ],
                    },
                  },
                }}
                getRowId={(row) => row.id}
              />
            )}
          </Drawer>
          <Dialog
            open={openUploadDialog}
            onClose={handleCloseUploadDialog}
            fullWidth
            maxWidth="md"
            PaperProps={{ sx: { borderRadius: 2, maxHeight: '90vh' } }}
          >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, py: 2 }}>
              <Box>
                <Typography variant="h6" component="div" sx={{ fontWeight: 500 }}>
                  File Upload
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Add your documents here
                </Typography>
              </Box>
              <IconButton aria-label="close" onClick={handleCloseUploadDialog}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ p: 3 }}>
              <Box
                sx={{
                  border: '1px dashed #ccc',
                  borderRadius: 1,
                  p: 3,
                  mb: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  backgroundColor: '#fafafa',
                  '&:hover': { backgroundColor: '#f5f5f5' },
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '150px',
                }}
                onDrop={handleFileDrop}
                onDragOver={handleDragOver}
                component="label"
                htmlFor="file-input"
              >
                <input
                  type="file"
                  id="file-input"
                  multiple
                  hidden
                  onChange={handleFileChange}
                />
                <FolderIcon sx={{ fontSize: 48, color: '#ddd', mb: 2 }} />
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Drop your files to start uploading
                </Typography>
                <Button
                  variant="outlined"
                  component="span"
                  sx={{ borderRadius: 1, textTransform: 'none', color: '#000000', borderColor: '#ccc', py: 0.5 }}
                >
                  Browse files
                </Button>
              </Box>
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 2 }}>
                Supports: pdf
              </Typography>
              {uploadFiles.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  {uploadFiles.map((file, index) => (
                    <Box key={index} sx={{ py: 1, borderBottom: '1px solid #eee' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2">{file.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatFileSize(file.size)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 1.5, width: 150, bgcolor: 'f9f9f9' }}>
                          <Typography variant="body2" color="text.secondary">
                            {file.detectedType}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
              {uploading && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Uploading... {uploadProgress}% Complete
                  </Typography>
                  <Box sx={{ width: '100%', bgcolor: '#eee', borderRadius: 1, height: 4 }}>
                    <Box sx={{ height: '100%', borderRadius: 1, bgcolor: '#8CD867', width: `${uploadProgress}%` }} />
                  </Box>
                </Box>
              )}
            </DialogContent>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2, borderTop: '1px solid #eee' }}>
              <Button
                variant="outlined"
                onClick={handleCloseUploadDialog}
                sx={{ mr: 1, color: 'text.primary', borderColor: '#ddd', textTransform: 'none' }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleUploadClick}
                disabled={uploadFiles.length === 0 || uploading}
                sx={{ bgcolor: '#8CD867', '&:hover': { bgcolor: '#7bc45a' }, textTransform: 'none' }}
              >
                Upload
              </Button>
            </Box>
          </Dialog>
        </>
      ) : (
        <PDFViewer
          fileUrl={selectedFile?.url}
          fileName={selectedFile?.name}
          onBack={handleBackToWorkflows}
          alerts={alerts}
        />
      )}
    </Box>
  );
};

export default Workflows;